import React, { FC } from "react";
import { Accordion } from "../Accordion";
import { CustomizationDetails } from "../CustomizationDetails";
import { QuantityIndicator } from "../QuantityIndicator";
import {
  BundleComponent,
  Customization,
  CheckoutOrderSummaryLocalizations,
} from "../../../../types/graphql/generated";
import {
  StyledBundle,
  StyledBundleItemContainer,
  StyledBundleLineItem,
  StyledBundleTitle,
  StyledBundleWithCustomizations,
  StyledCustomizations,
  StyledHeadingContainer,
  StyledInterval,
  StyledQuantity,
  StyledSubscribe,
  StyledSubscribeDetails,
  StyledSubscribeLogo,
} from "./index.styled";
import { SubscriptionIconSvg } from "../../../icons/SubscriptionIconSvg";

interface BundleLineItemProps {
  customizations?: Customization[] | null;
  quantity: number;
  title: string;
  subscriptionIntervalInDays?: number | null;
}

interface BundleDetailsProps {
  bundleItems: BundleComponent[];
  localization?: CheckoutOrderSummaryLocalizations | null;
}

const BundleLineItem: FC<BundleLineItemProps> = ({
  customizations,
  quantity,
  title,
  subscriptionIntervalInDays,
}) =>
  customizations?.length ? (
    <>
      <StyledBundleLineItem>
        <StyledQuantity>
          <QuantityIndicator quantity={quantity} />
        </StyledQuantity>
        <StyledBundleWithCustomizations>
          <div>{title}</div>
          <StyledCustomizations>
            <CustomizationDetails customizations={customizations} isNested />
          </StyledCustomizations>
        </StyledBundleWithCustomizations>
      </StyledBundleLineItem>
      {subscriptionIntervalInDays && (
        <StyledSubscribe>
          <StyledSubscribeLogo>
            <SubscriptionIconSvg />
          </StyledSubscribeLogo>
          <StyledSubscribeDetails>
            <div>Subscription</div>
            <StyledInterval>
              Deliver every {subscriptionIntervalInDays} days
            </StyledInterval>
          </StyledSubscribeDetails>
        </StyledSubscribe>
      )}
    </>
  ) : (
    <>
      <StyledBundleLineItem>
        <StyledQuantity>
          <QuantityIndicator quantity={quantity} />
        </StyledQuantity>
        <div>{title}</div>
      </StyledBundleLineItem>
      {subscriptionIntervalInDays && (
        <StyledSubscribe>
          <StyledSubscribeLogo>
            <SubscriptionIconSvg />
          </StyledSubscribeLogo>
          <StyledSubscribeDetails>
            <div>Subscription</div>
            <StyledInterval>
              Deliver every {subscriptionIntervalInDays} days
            </StyledInterval>
          </StyledSubscribeDetails>
        </StyledSubscribe>
      )}
    </>
  );

export const BundleDetails: FC<BundleDetailsProps> = ({
  bundleItems,
  localization,
}) => {
  const {
    whatsIncludedText = "What's included",
    bundleItemDefaultText = "Bundle Item",
  } = localization || {};

  return (
    <Accordion title={whatsIncludedText}>
      <StyledBundle>
        {bundleItems?.map(({ title, subtitle, componentPriceItems }, i) => (
          <StyledBundleItemContainer key={`bundle-items-${i}`}>
            <StyledHeadingContainer>
              <StyledBundleTitle>{title}</StyledBundleTitle>
              <span>{subtitle}</span>
            </StyledHeadingContainer>

            {componentPriceItems?.map((priceItem, i) => (
              <BundleLineItem
                customizations={priceItem?.customizations}
                quantity={priceItem?.quantity || 0}
                title={
                  priceItem?.item?.title || `${bundleItemDefaultText} ${i + 1}`
                }
                key={`bundle-item-${i}`}
                subscriptionIntervalInDays={
                  priceItem?.subscriptionIntervalInDays
                }
              />
            ))}
          </StyledBundleItemContainer>
        ))}
      </StyledBundle>
    </Accordion>
  );
};
